import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  Typography,
  Link,
} from '@mui/material';
import React, { FC, useMemo, useState, useEffect } from 'react';
import { TrainingTeam, TrainingTeamAssignee } from '../../../models/training.models';
import { formatDuration, getElevationMeasurementUnit, getMeasurementUnit } from '../../../helpers/general.helpers';
import { UserAvatar } from '../../user-avatar/user-avatar.component';
import useService from '../../../hooks/service.hook';
import TrainingsService from '../../../services/trainings.service';
import { useSnackbar } from 'notistack';
import CoachAthleteTrainingModal from '../../coach-athlete-training-modal';
import { Training } from '../../../models/training.models';
import { palette } from '../../../theme';

export interface MembersTabProps {
  team: TrainingTeam;
  type: 'athlete' | 'coach' | 'guardian';
  trainingId: string;
  training: Training;
  onRefresh?: () => void;
  onUnreadCommentsCleared?: (clearedCount: number) => void;
}

interface AttendanceState {
  [memberId: number]: boolean | null;
}

const buildAttendanceState = (assignees: TrainingTeamAssignee[]): AttendanceState => {
  const state: AttendanceState = {};
  assignees.forEach((assignee) => {
    state[assignee.memberId] = assignee.attended;
  });
  return state;
};

const hasAttendanceChanges = (saved: AttendanceState, draft: AttendanceState): boolean =>
  Object.keys(draft).some((memberId) => saved[+memberId] !== draft[+memberId]);

const MembersTab: FC<MembersTabProps> = (props) => {
  const trainingsService = useService(TrainingsService);
  const { enqueueSnackbar } = useSnackbar();
  const [savedAttendance, setSavedAttendance] = useState<AttendanceState>({});
  const [draftAttendance, setDraftAttendance] = useState<AttendanceState>({});
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [showCoachAthleteModal, setShowCoachAthleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [localAssignees, setLocalAssignees] = useState<TrainingTeamAssignee[]>(props.team?.assignees || []);

  const showAttendance = props.type === 'coach' && (props.training.attendanceEnabled ?? true);

  useEffect(() => {
    if (props.team?.assignees) {
      const initialState = buildAttendanceState(props.team.assignees);
      setSavedAttendance(initialState);
      setDraftAttendance(initialState);
      setLocalAssignees(props.team.assignees);
    }
  }, [props.team]);

  const hasPendingChanges = showAttendance && hasAttendanceChanges(savedAttendance, draftAttendance);

  const hasWorkoutData = (duration: number) => duration > 0;

  const getLastName = (fullName: string) => {
    const nameParts = fullName.trim().split(' ');
    return nameParts[nameParts.length - 1];
  };

  const sortedAssignees = useMemo(
    () =>
      [...localAssignees].sort((a, b) =>
        getLastName(a.name).toLowerCase().localeCompare(getLastName(b.name).toLowerCase())
      ),
    [localAssignees]
  );

  const getRsvpChipProps = (rsvpStatus: string) => {
    if (rsvpStatus === 'a') {
      return { label: 'Yes', bgcolor: palette.success.light, color: palette.success.dark };
    }
    if (rsvpStatus === 'd') {
      return { label: 'No', bgcolor: palette.error.light, color: palette.error.dark };
    }
    return { label: 'N/A', bgcolor: palette.grey[200], color: palette.text.disabled };
  };

  const handleMemberClick = (assignee: any) => {
    if (props.type === 'coach') {
      setSelectedMember(assignee);
      setShowCoachAthleteModal(true);
    }
  };

  const handleAttendanceToggle = (memberId: number) => {
    setDraftAttendance((prev) => ({
      ...prev,
      [memberId]: !prev[memberId],
    }));
  };

  const handleCancelAttendance = () => {
    setDraftAttendance(savedAttendance);
  };

  const handleSaveAttendance = async () => {
    const assignees = Object.entries(draftAttendance)
      .filter(([memberId, attended]) => savedAttendance[+memberId] !== attended)
      .map(([memberId, attended]) => ({ member_id: +memberId, attended }));

    if (assignees.length === 0) return;

    setSavingAttendance(true);
    try {
      await trainingsService.updateAttendedStatusBulk(props.trainingId, assignees);
      setSavedAttendance({ ...draftAttendance });
      enqueueSnackbar('Attendance updated successfully!', {
        autoHideDuration: 2000,
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
      if (props.onRefresh) props.onRefresh();
    } catch {
      enqueueSnackbar('Failed to update attendance. Please try again.', {
        autoHideDuration: 3000,
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    } finally {
      setSavingAttendance(false);
    }
  };

  const clearMemberUnreadComments = (memberId?: number) => {
    if (memberId !== undefined) {
      setLocalAssignees((prevAssignees) => {
        const member = prevAssignees.find((a) => a.memberId === memberId);
        const clearedCount = member?.unreadComments || 0;
        const updated = prevAssignees.map((a) => (a.memberId === memberId ? { ...a, unreadComments: 0 } : a));
        if (props.onUnreadCommentsCleared) props.onUnreadCommentsCleared(clearedCount);
        return updated;
      });
    } else {
      if (props.onUnreadCommentsCleared) props.onUnreadCommentsCleared(0);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'fit-content' }}>
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        {/* Spacer: avatar + name */}
        <Box sx={{ minWidth: 44 }} />
        <Box sx={{ flex: '0 0 160px' }} />

        {/* Push stats to the right */}
        <Box sx={{ flex: 1 }} />

        {/* Stat column headers */}
        <Box sx={{ width: 72, textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            Duration
          </Typography>
        </Box>
        <Box sx={{ width: 72, textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            Distance
          </Typography>
        </Box>
        <Box sx={{ width: 72, textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            Ascent
          </Typography>
        </Box>

        {/* RSVP + Attendance */}
        <Typography
          variant="caption"
          sx={{
            width: 80,
            textAlign: 'center',
            color: 'text.disabled',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          RSVP
        </Typography>
        {showAttendance && (
          <Typography
            variant="caption"
            sx={{
              width: 80,
              textAlign: 'center',
              color: 'text.disabled',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Attendance
          </Typography>
        )}
      </Box>

      {/* Members list */}
      <List sx={{ width: '100%', p: 0 }}>
        {sortedAssignees.map((assignee, index) => {
          const rsvp = getRsvpChipProps(assignee.rsvpStatus);
          const attended = draftAttendance[assignee.memberId];
          const isPending = savedAttendance[assignee.memberId] !== attended;
          const withData = hasWorkoutData(assignee.duration);
          const isClickable = props.type === 'coach';

          return (
            <React.Fragment key={assignee.memberId}>
              <ListItem sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
                {/* Avatar */}
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <UserAvatar src={assignee.profileImage} name={assignee.name} size={36} fontSize="0.875rem" />
                </ListItemAvatar>

                {/* Name */}
                <Box sx={{ flex: '0 0 160px', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Link
                    component="button"
                    onClick={() => handleMemberClick(assignee)}
                    underline="none"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: withData && isClickable ? palette.primary.main : palette.text.primary,
                      cursor: isClickable ? 'pointer' : 'default',
                      '&:hover': { textDecoration: isClickable ? 'underline' : 'none' },
                    }}
                  >
                    {assignee.name}
                  </Link>
                  {assignee.unreadComments != null && assignee.unreadComments > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'white',
                        bgcolor: palette.error.main,
                        borderRadius: '12px',
                        px: '6px',
                        py: '2px',
                        lineHeight: 1,
                      }}
                    >
                      {assignee.unreadComments}
                    </Typography>
                  )}
                </Box>

                {/* Push stats to the right */}
                <Box sx={{ flex: 1 }} />

                {/* Inline workout stats — fixed-width columns so they align vertically */}
                <Box sx={{ width: 72, textAlign: 'center' }}>
                  {withData && (
                    <Typography variant="body2" fontWeight={600} color={palette.primary.main}>
                      {formatDuration(assignee.duration.toString())} hr
                    </Typography>
                  )}
                </Box>
                <Box sx={{ width: 72, textAlign: 'center' }}>
                  {withData && (
                    <Typography variant="body2" fontWeight={600} color={palette.primary.main}>
                      {assignee.distance} {getMeasurementUnit()}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ width: 72, textAlign: 'center' }}>
                  {withData && assignee.ascent != null && assignee.ascent > 0 && (
                    <Typography variant="body2" fontWeight={600} color={palette.primary.main}>
                      {assignee.ascent.toLocaleString()} {getElevationMeasurementUnit()}
                    </Typography>
                  )}
                </Box>

                {/* RSVP chip */}
                <Box sx={{ width: 80, display: 'flex', justifyContent: 'center' }}>
                  <Chip
                    label={rsvp.label}
                    size="small"
                    sx={{
                      bgcolor: rsvp.bgcolor,
                      color: rsvp.color,
                      fontWeight: 600,
                      fontSize: '12px',
                      height: 24,
                      borderRadius: '6px',
                    }}
                  />
                </Box>

                {/* Attendance circle */}
                {showAttendance && (
                  <Box sx={{ width: 80, display: 'flex', justifyContent: 'center' }}>
                    <Box
                      onClick={() => handleAttendanceToggle(assignee.memberId)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        bgcolor: attended ? palette.primary.main : 'transparent',
                        border: `2px ${isPending ? 'dashed' : 'solid'} ${
                          attended ? palette.primary.main : palette.grey[400]
                        }`,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: palette.primary.main,
                          opacity: 0.85,
                        },
                      }}
                    />
                  </Box>
                )}
              </ListItem>
              {index < sortedAssignees.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>

      {hasPendingChanges && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            mt: 1,
            borderTop: `1px solid ${palette.divider}`,
            position: 'sticky',
            bottom: 0,
            bgcolor: palette.background.paper,
          }}
        >
          <Button variant="outlined" onClick={handleCancelAttendance} disabled={savingAttendance}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAttendance}
            disabled={savingAttendance}
            startIcon={savingAttendance ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Save
          </Button>
        </Box>
      )}

      {/* Coach athlete training modal */}
      {selectedMember && (
        <CoachAthleteTrainingModal
          open={showCoachAthleteModal}
          onClose={() => {
            setShowCoachAthleteModal(false);
            setSelectedMember(null);
          }}
          teamId={String(props.team.id)}
          memberId={selectedMember.memberId}
          trainingId={props.trainingId}
          memberPreview={{
            name: selectedMember.name,
            profileImage: selectedMember.profileImage,
          }}
          onTrainingUpdated={() => {
            props.onRefresh?.();
            clearMemberUnreadComments(selectedMember.memberId);
          }}
        />
      )}
    </Box>
  );
};

export default MembersTab;
